import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ContactSalesModal from "./ContactSalesModal";
import { BillingAPI } from "@/api/billing";

interface Tier {
  name: string;
  price: string;
  priceNote?: string;
  description: string;
  features: string[];
  cta: "trial" | "contact" | "unavailable";
  badge?: string;
}

const tiers: Tier[] = [
  {
    name: "Community Edition",
    price: "Free",
    priceNote: "Forever, limited functionality",
    description: "Free forever, for individual developers evaluating Orbit8.",
    features: [
      "3 Scans / Month",
      "1 User included",
      "1 Manual Upload / Month",
    ],
    cta: "trial",
    badge: "Forever Free",
  },
  {
    name: "Professional",
    price: "$99",
    priceNote: "per month",
    description: "For growing teams that need full reporting and RBAC.",
    features: [
      "100 Scans / Month",
      "5 Users included (+$5/User/Month Extra)",
      "15 Manual Upload / Month",
      "Full Dashboard & Analytics",
      "Component Hub Export, Comprehensive Excel Reports",
      "GitHub App integration",
      "Licence Policy Customisation, Conditional Alerts, RBAC",
      "5-Day Free Trial, No Card required",
    ],
    cta: "unavailable",
  },
  {
    name: "Business",
    price: "$299",
    priceNote: "per month",
    description: "For teams needing deeper analysis and scan rollover.",
    features: [
      "300 Scans / Month",
      "20 Users included (+$5/User/Month Extra)",
      "50 Manual Upload / Month",
      "Everything in Professional",
      "Dependency Analysis View, Provenance Data, CBOM",
      "SBOM Upload/Import, Evaluator Rules Setting",
      "Scan rollover to next month, Resets at year end",
    ],
    cta: "unavailable",
  },
  {
    name: "Enterprise",
    price: "Custom",
    priceNote: "Contact sales",
    description: "Custom pricing for organisations that need depth, datasets, and team enablement.",
    features: [
      "Unlimited Scans, Projects & Users",
      "Everything in Business",
      "SSO/SAML, Jira/Slack/Webhook Integrations",
      "On-Premises / Private Cloud Deployment",
      "Dedicated Customer Success Manager, Custom Reporting",
      "CI/CD integration, API Availability",
      "Dedicated Slack, Priority Support",
    ],
    cta: "contact",
  },
];

const addOns = [
  { name: "CRA Assessment", price: null },
  { name: "ISO 5230 Assessment", price: null },
  { name: "Provenance Dataset", price: "$50" },
  { name: "Crypto Dataset", price: "$50" },
];

const Pricing = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactPlan, setContactPlan] = useState<string | undefined>();

  const openContact = (planName?: string) => {
    setContactPlan(planName);
    setContactOpen(true);
  };

  return (
    <>
      <section className="relative z-10 px-6 pb-12 pt-32">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-bold tracking-[-0.01em] text-foreground md:text-5xl">
            Simple, <span className="bg-gradient-to-r from-primary to-[hsl(174,45%,45%)] bg-clip-text text-transparent">Transparent</span> Pricing
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-light leading-relaxed text-muted-foreground">
            Choose a plan that fits your team. Start free, scale when you need more depth, datasets,
            and enablement.
          </p>
        </div>
      </section>

      <section className="relative z-10 px-6 pb-16">
        <div className="container mx-auto grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((tier) => (
            <article
              key={tier.name}
              className="group flex flex-col rounded-2xl border border-border bg-card/80 p-8 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
            >
              {tier.badge ? (
                <span className="mb-3 self-start rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold tracking-wider text-primary">
                  {tier.badge}
                </span>
              ) : null}
              <h3 className="text-2xl font-bold text-foreground">{tier.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-4xl font-bold text-foreground">{tier.price}</span>
                {tier.priceNote ? (
                  <span className="text-sm font-light text-muted-foreground">{tier.priceNote}</span>
                ) : null}
              </div>
              <p className="mt-3 text-sm font-light leading-relaxed text-muted-foreground">
                {tier.description}
              </p>

              <ul className="mt-6 flex-1 space-y-3">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm text-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="font-light leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                {tier.cta === "trial" ? (
                  <Button asChild className="w-full">
                    <Link to="/signup">
                      Use Community Edition (Free)
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : tier.cta === "unavailable" ? (
                  <>
                    <Button type="button" variant="outline" disabled className="w-full opacity-60">
                      Coming Soon
                    </Button>
                    <p className="mt-2 text-center text-xs font-light text-muted-foreground">
                      Paid plans are temporarily unavailable.
                    </p>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openContact(tier.name)}
                    className="w-full border-primary/30 text-primary transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
                  >
                    Reach Out to Us
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/*<section className="relative z-10 px-6 pb-24">
        <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card/80 p-8 backdrop-blur-sm md:p-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-2xl font-bold text-foreground">Add Ons</h3>
              <p className="mt-2 text-sm font-light text-muted-foreground">
                Tailor your plan with assessments and datasets that fit your needs.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => openContact("Add Ons")}
              className="shrink-0 border-primary/30 text-primary transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
            >
              Reach Out to Us
            </Button>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {addOns.map((addOn) => (
              <article
                key={addOn.name}
                className="flex items-center justify-between rounded-xl border border-border bg-white/40 p-4 transition-colors hover:border-primary/30"
              >
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{addOn.name}</span>
                </div>
                {addOn.price ? (
                  <span className="text-sm font-semibold text-primary">{addOn.price}</span>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>*/}

      <ContactSalesModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        planName={contactPlan}
        confirmOnSuccess
        onSubmit={async (data) => {
          await BillingAPI.publicContactSales({ ...data, source: "pricing" });
        }}
      />
    </>
  );
};

export default Pricing;
